// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Button, Fade, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { dollars } from 'Utils/numberFormat';
import { RootState } from 'Store';
import { GetOrgThunk, UpdateOverageRateThunk } from 'Features/admin/adminThunk';
import HistoryList from 'Components/HistoryList';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

/**
 * What a credit beyond the account's balance costs, and why (AUT-6571): the admin's override when one is set,
 * otherwise the overage ladder spot the last 12 months of usage reaches, with the license-fee column the account's
 * setting makes apply. Admins with pricing access override it here with a note, or revert to the ladder; every
 * change is listed under the line. Nothing on a pay-as-you-go account — it has no balance to exceed.
 */
export const OverageRate = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org } = useAppSelector((state: RootState) => state.admin);
  const overage = org?.overageRate;
  const history = org?.overageRateHistory || [];

  const [editMode, setEditMode] = useState<'set' | 'revert' | null>(null);
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!overage) return null;

  const feeAdded = overage.licenseFeeMode === 'added';
  const canSave = !!notes.trim() && (editMode === 'revert' || parseFloat(rate) > 0) && !saving;

  const cancelEdit = () => {
    setEditMode(null);
    setRate('');
    setNotes('');
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const rateToSave = editMode === 'revert' ? null : parseFloat(rate);
    const result = await dispatch(UpdateOverageRateThunk(rateToSave, notes.trim()));
    if (result.success) {
      await dispatch(GetOrgThunk(org._id));
      cancelEdit();
    } else {
      setError(result.message || 'Failed to update the overage rate');
    }
    setSaving(false);
  };

  const renderSource = () => {
    if (overage.override) {
      const { override } = overage;
      const base = `${dollars(override.rate)}${feeAdded ? ' + 10% license fee' : ''}`;
      return `Override: ${base}, set by ${override.changedBy} on ${moment(override.createdAt).format('LL')}: ${override.notes}`;
    }
    return `${overage.spot.displayName} spot — ${dollars(overage.usageDollars)} of usage in the last 12 months.`;
  };

  return (
    <Box sx={styles.root}>
      <Typography sx={styles.rateLine}>
        {`${dollars(overage.applied)} per credit beyond the balance${feeAdded ? ' (includes the 10% license fee)' : ''}`}
      </Typography>
      <Typography variant="body2" sx={styles.sourceText}>{renderSource()}</Typography>
      {!editMode && (
        <Box sx={styles.buttonRow}>
          <Button disabled={!user?.pricingAccess} sx={styles.button} onClick={() => setEditMode('set')} color="primary" variant="outlined">
            Edit Overage Rate
          </Button>
          {overage.override && (
            <Button disabled={!user?.pricingAccess} sx={styles.button} onClick={() => setEditMode('revert')} variant="text">
              Revert to Ladder Rate
            </Button>
          )}
        </Box>
      )}
      {editMode && (
        <Box sx={styles.form}>
          <Typography>New Overage Rate</Typography>
          {editMode === 'set' ? (
            <TextField
              id="overageRate"
              variant="standard"
              type="number"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { 'aria-label': 'New Overage Rate' } }}
              onChange={(e) => setRate(e.target.value)}
              value={rate}
              sx={styles.rateField}
            />
          ) : (
            <Typography>Ladder rate</Typography>
          )}
          <TextField
            id="overageRateNotes"
            label="Notes (required)"
            variant="standard"
            multiline
            minRows={2}
            onChange={(e) => setNotes(e.target.value)}
            value={notes}
            sx={styles.notesField}
          />
          {error && <Typography variant="body2" sx={styles.error}>{error}</Typography>}
          <Box sx={styles.buttonRow}>
            <Button sx={styles.button} disabled={!canSave} onClick={save} color="primary" variant="contained">
              <Fade in={saving}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!saving}>
                <span>{editMode === 'revert' ? 'Revert Rate' : 'Save Rate'}</span>
              </Fade>
            </Button>
            <Button sx={styles.button} onClick={cancelEdit} variant="text">Cancel</Button>
          </Box>
        </Box>
      )}
      <HistoryList
        entries={history}
        getKey={(entry) => entry._id}
        renderRow={(entry) => (
          <Box sx={styles.historyRow}>
            <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
            <Typography variant="body2" sx={styles.historyChange}>
              {typeof entry.rate === 'number' ? `${dollars(entry.rate)} per credit` : 'Ladder rate'}
            </Typography>
            <Typography variant="body2">{entry.changedBy}</Typography>
            <Typography variant="body2" sx={styles.historyNotes}>{entry.notes}</Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default OverageRate;
